import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Phone, MessageSquareText, Mail, CalendarHeart } from "lucide-react";
import { Reveal, Stagger, StaggerItem, MaskedLine, SectionLabel } from "@/components/Reveal";
import IntakeForm from "@/components/IntakeForm";
import { PHONE_DISPLAY, PHONE_TEL, PHONE_SMS, EMAIL, EMAIL_MAILTO, TEAM, IMAGES, usePageTitle } from "@/lib/data";

const methods = [
  {
    id: "call",
    icon: Phone,
    title: "Call us",
    detail: PHONE_DISPLAY,
    note: "Call anytime — if we're with a homeowner, leave a voicemail and we'll call you back quietly, usually the same day.",
    href: PHONE_TEL,
    action: "Call now",
  },
  {
    id: "text",
    icon: MessageSquareText,
    title: "Text us",
    detail: PHONE_DISPLAY,
    note: "Prefer to write it out? A simple text is a perfectly good way to start.",
    href: PHONE_SMS,
    action: "Send a text",
  },
  {
    id: "email",
    icon: Mail,
    title: "Email us",
    detail: EMAIL,
    note: "Share as much or as little as you like. We reply personally, usually within a day.",
    href: EMAIL_MAILTO,
    action: "Write an email",
  },
  {
    id: "schedule",
    icon: CalendarHeart,
    title: "Schedule a consultation",
    detail: "Free & confidential",
    note: "Use the private form below — tell us the day and time that suits you, and we'll call you then.",
    href: "#private-form",
    action: "Choose a time",
  },
];

export default function Contact() {
  usePageTitle("Contact");
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get("situation") || "";
  return (
    <>
      <section data-testid="contact-hero" className="pt-44 pb-20 grain relative overflow-hidden">
        <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="text-[11px] tracking-[0.3em] uppercase font-semibold text-sage-deep mb-8"
          >
            Contact
          </motion.p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-medium text-night max-w-4xl" data-testid="contact-headline">
            <MaskedLine delay={0.25}>Start a private</MaskedLine>
            <MaskedLine delay={0.42}>
              <em className="not-italic text-sage-deep">conversation.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-lg leading-relaxed text-charcoal max-w-2xl"
          >
            However you reach us — phone, text, email, or the quiet form below — you'll talk to a real,
            local person. Share only what you're comfortable sharing. A first name and a way to reach
            you is enough.
          </motion.p>
        </div>
      </section>

      <section data-testid="contact-methods" className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {methods.map((m) => (
              <StaggerItem key={m.id}>
                <a
                  href={m.href}
                  data-testid={`contact-method-${m.id}`}
                  className="group flex flex-col h-full bg-bone rounded-3xl p-8 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(26,36,33,0.18)] transition-all duration-500"
                >
                  <m.icon size={26} strokeWidth={1.5} className="text-sage-deep" />
                  <h2 className="font-serif text-2xl text-night mt-5">{m.title}</h2>
                  <p className="text-sm font-medium text-coastal-deep mt-1">{m.detail}</p>
                  <p className="text-sm text-charcoal leading-relaxed mt-4 flex-1">{m.note}</p>
                  <span className="mt-6 text-xs tracking-[0.18em] uppercase font-semibold text-sage-deep group-hover:tracking-[0.26em] transition-all duration-500">
                    {m.action}
                  </span>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section data-testid="contact-team" className="pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-12">
          <Reveal>
            <SectionLabel>Your local team</SectionLabel>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 gap-5">
            {TEAM.map((t) => (
              <StaggerItem key={t.name}>
                <div data-testid={`team-card-${t.name.split(" ")[0].toLowerCase()}`} className="bg-coastal-pale rounded-3xl p-8 h-full">
                  <h2 className="font-serif text-3xl text-night">{t.name}</h2>
                  <p className="text-sm text-charcoal mt-1">{t.role}</p>
                  <div className="mt-5 flex flex-col gap-2 text-sm">
                    <a href={EMAIL_MAILTO} data-testid={`team-email-${t.name.split(" ")[0].toLowerCase()}`} className="flex items-center gap-2 text-sage-deep hover:text-night transition-colors duration-300">
                      <Mail size={14} strokeWidth={1.75} /> {EMAIL}
                    </a>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section id="private-form" data-testid="contact-form-section" className="py-24 lg:py-32 bg-bone grain scroll-mt-20">
        <div className="relative z-[2] mx-auto max-w-4xl px-6 lg:px-12">
          <Reveal>
            <SectionLabel>The private intake form</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-night leading-tight max-w-2xl">
              A quiet way to begin.
            </h2>
            <p className="mt-6 text-charcoal leading-relaxed text-lg max-w-2xl">
              This goes directly to our Central Florida team — nowhere else. Nothing here obligates you
              to anything, and every field is optional except a way to reach you.
            </p>
          </Reveal>
          <Reveal>
            <div className="rounded-[2.5rem] overflow-hidden mb-14 shadow-[0_30px_70px_-25px_rgba(26,36,33,0.25)]">
              <img src={IMAGES.key} alt="A brass key resting on a welcome mat, warm light spilling from inside — the next chapter" className="w-full h-[300px] sm:h-[360px] object-cover" />
            </div>
          </Reveal>
          <Reveal className="mt-12" delay={0.15}>
            <IntakeForm initialSituation={prefill} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
